import dayjs from '@/plugins/dayjs'
import type { AuditEntry, AuditAction } from '@/types/audit'
import { createSeededRandom, pick, randomInt } from '@/utils/randomSeeded'
import { usersFixture } from './users.fixture'
import { customersFixture } from './customers.fixture'
import { campaignsFixture } from './campaigns.fixture'
import { emailTemplatesFixture, smsTemplatesFixture } from './templates.fixture'

const CHANNEL_SETS = ['Email', 'Email, SMS', 'SMS', 'Email, SMS, WhatsApp', 'WhatsApp']

/** Which actions each role realistically performs, so the journal reads true to life. */
const ACTIONS_BY_ROLE: Record<string, AuditAction[]> = {
  owner: ['settings.updated', 'team.invited', 'team.role_changed', 'campaign.sent', 'customer.updated', 'integration.connected'],
  admin: ['customer.created', 'customer.updated', 'customer.archived', 'template.updated', 'campaign.sent', 'segment.created'],
  operator: ['customer.created', 'consent.requested', 'customer.updated', 'consent.requested', 'customer.created'],
  marketing: ['campaign.sent', 'campaign.scheduled', 'template.updated', 'template.created', 'segment.created', 'consent.requested'],
  viewer: [],
}

function randomIp(random: () => number): string {
  return `86.${randomInt(20, 250, random)}.${randomInt(1, 254, random)}.${randomInt(1, 254, random)}`
}

/** ~170 realistic entries over the last 60 days; deterministic so every reload tells the same story. */
export function generateAuditEntries(count = 170, seed = 7): AuditEntry[] {
  const random = createSeededRandom(seed)
  const actors = usersFixture.filter((u) => ACTIONS_BY_ROLE[u.role]?.length)
  const entries: AuditEntry[] = []

  for (let i = 0; i < count; i++) {
    const actor = pick(actors, random)
    const action = pick(ACTIONS_BY_ROLE[actor.role] ?? ['customer.updated'], random)
    const hoursAgo = randomInt(1, 60 * 24, random)
    const date = dayjs().subtract(hoursAgo, 'hour').subtract(randomInt(0, 59, random), 'minute').toISOString()
    const base = {
      id: `aud_fx_${String(i + 1).padStart(4, '0')}`,
      actorId: actor.id,
      actorName: `${actor.firstName} ${actor.lastName}`,
      action,
      date,
      ip: randomIp(random),
    }

    switch (action) {
      case 'customer.created':
      case 'customer.updated':
      case 'customer.archived':
      case 'consent.requested': {
        const customer = pick(customersFixture, random)
        entries.push({
          ...base,
          targetType: 'customer',
          targetId: customer.id,
          targetName: `${customer.firstName} ${customer.lastName}`,
          details: action === 'consent.requested' ? `Prin ${pick(['SMS', 'email', 'WhatsApp'], random)}` : undefined,
        })
        // Some requested consents get signed by the customer a little later.
        if (action === 'consent.requested' && random() < 0.6) {
          entries.push({
            id: `${base.id}_signed`,
            actorId: customer.id,
            actorName: `${customer.firstName} ${customer.lastName}`,
            actorIsCustomer: true,
            action: 'consent.recorded',
            targetType: 'customer',
            targetId: customer.id,
            targetName: undefined,
            details: `Canale: ${pick(CHANNEL_SETS, random)}`,
            date: dayjs(date).add(randomInt(5, 600, random), 'minute').toISOString(),
            ip: randomIp(random),
          })
        }
        break
      }
      case 'campaign.sent':
      case 'campaign.scheduled': {
        const campaign = pick(campaignsFixture, random)
        entries.push({ ...base, targetType: 'campaign', targetId: campaign.id, targetName: `„${campaign.name}”`, details: `${campaign.audienceSize} destinatari` })
        break
      }
      case 'template.created':
      case 'template.updated': {
        const template = pick([...emailTemplatesFixture, ...smsTemplatesFixture], random)
        entries.push({ ...base, targetType: 'template', targetId: template.id, targetName: `„${template.name}”` })
        break
      }
      case 'segment.created':
        entries.push({ ...base, targetType: 'segment', targetName: `„${pick(['Clienți VIP', 'Inactivi 90 zile', 'București — SMS', 'Clienți noi luna aceasta'], random)}”` })
        break
      case 'team.invited':
      case 'team.role_changed': {
        const member = pick(usersFixture.filter((u) => u.id !== actor.id), random)
        entries.push({
          ...base,
          targetType: 'user',
          targetId: member.id,
          targetName: `${member.firstName} ${member.lastName}`,
          details: action === 'team.role_changed' ? 'Rol nou: Operator' : undefined,
        })
        break
      }
      case 'settings.updated':
        entries.push({ ...base, targetType: 'settings', details: pick(['Adresa companiei', 'Preferințe notificări', 'Numele companiei'], random) })
        break
      case 'integration.connected':
        entries.push({ ...base, targetType: 'integration', targetName: pick(['Brevo (email)', 'Twilio (SMS)', 'WhatsApp Business'], random) })
        break
      default:
        entries.push({ ...base, targetType: 'company' })
    }
  }

  // Clamp any "signed later" entries that landed in the future.
  const now = dayjs()
  return entries
    .map((e) => (dayjs(e.date).isAfter(now) ? { ...e, date: now.subtract(randomInt(1, 30, random), 'minute').toISOString() } : e))
    .sort((a, b) => dayjs(b.date).valueOf() - dayjs(a.date).valueOf())
}

/** Hand-written entries for the showcase customer (Ion Popescu), so his profile timeline tells a complete story. */
const notableEntries: AuditEntry[] = [
  {
    id: 'aud_notable_01',
    actorId: 'usr_0002',
    actorName: 'Mihai Ionescu',
    action: 'consent.requested',
    targetType: 'customer',
    targetId: 'cus_notable_01',
    targetName: 'Ion Popescu',
    details: 'Prin SMS',
    date: dayjs().subtract(48, 'day').hour(13).minute(5).toISOString(),
    ip: '86.120.14.201',
  },
  {
    id: 'aud_notable_02',
    actorId: 'cus_notable_01',
    actorName: 'Ion Popescu',
    actorIsCustomer: true,
    action: 'consent.recorded',
    targetType: 'customer',
    targetId: 'cus_notable_01',
    details: 'Canale: Email, SMS, WhatsApp · Link SMS',
    date: dayjs().subtract(48, 'day').hour(13).minute(32).toISOString(),
    ip: '86.34.201.77',
  },
  {
    id: 'aud_notable_03',
    actorId: 'usr_0001',
    actorName: 'Andreea Popescu',
    action: 'customer.updated',
    targetType: 'customer',
    targetId: 'cus_notable_01',
    targetName: 'Ion Popescu',
    details: 'Etichete: VIP, Fidel',
    date: dayjs().subtract(12, 'day').hour(10).minute(14).toISOString(),
    ip: '86.120.14.201',
  },
]

export const auditFixture: AuditEntry[] = [...notableEntries, ...generateAuditEntries()].sort(
  (a, b) => dayjs(b.date).valueOf() - dayjs(a.date).valueOf(),
)
